/**
 * JS-1082: the deep-link contract between a Desk idea (the screener drawer,
 * an edition card) and the ticker dashboard's "viewing in context of" mode.
 *
 * Convention chosen: a SHAREABLE query-string link
 * (`/ticker/:ticker?ideaLedgerId=...&ideaOrigin=...&ideaRowId=...`), NOT the
 * one-shot `navigate(path, { state })` convention JS-1081 established for
 * the Watch-wizard pre-fill (`deskIdeaWatchPrefill.ts` /
 * `MultiStrategyScreenTable.tsx`'s `openPrefilledWatch`). The two flows have
 * genuinely different requirements:
 *
 *   - JS-1081's Watch pre-fill hands the wizard a RICH, non-serializable
 *     object (`WatchWizardInitialState`, containing a full `MultiLegDraft`)
 *     that only ever needs to survive ONE same-tab navigation -- router
 *     state is the right tool, and `WatchCreatePage.tsx` deliberately clears
 *     it after first read (one-shot consumption) so a browser Back button
 *     can never re-hydrate stale data.
 *   - JS-1082's ticker view is explicitly meant to be reopened, bookmarked,
 *     reloaded, and shared (the ticket's own wireframe treats it as a real
 *     page, and `WatchCreatePage.tsx`'s OWN `sourceWatchId`/`mode` query
 *     params are the existing precedent for exactly this "resolve context
 *     from an id, safe across reload" shape). Router state does not survive
 *     a reload or a shared link; a query param does. Carrying the ledger id
 *     (not a pre-fetched payload) also means the destination page always
 *     re-resolves against the LIVE screener row, never a possibly-stale
 *     snapshot captured at link-construction time.
 *
 * `ideaOrigin` + `ideaRowId` are NOT needed to resolve context (the backend
 * endpoint resolves purely by `ideaLedgerId`) -- they exist ONLY so "back to
 * idea" can return to the RIGHT surface and reopen the RIGHT drawer, since
 * the ticker view has no other way to know where the visitor came from.
 */

export const TICKER_CONTEXT_LEDGER_ID_PARAM = "ideaLedgerId";
export const TICKER_CONTEXT_ORIGIN_PARAM = "ideaOrigin";
export const TICKER_CONTEXT_ROW_ID_PARAM = "ideaRowId";
export const TICKER_CONTEXT_AS_OF_PARAM = "ideaAsOf";

export type TickerContextOrigin = "screener" | "edition" | "ledger";

export interface TickerContextLinkInput {
  ticker: string;
  ledgerId: string;
  origin: TickerContextOrigin;
  /**
   * The row's identity on the origin surface, for reopening its drawer/card.
   *
   * JS-1094 (demo blocker FIX 2): for a "ledger" origin, this is NOT read at
   * all -- a ledger-origin outbound link carries no `rowId` param, since the
   * ledger table's row identity (`row.ledger_id`) IS `ledgerId` above, not a
   * SEPARATE screener `row_id`. The prior behavior smuggled `row.ledger_id`
   * through this same `rowId` field, which the ticker dashboard's
   * `reconcileBackToIdeaRowId` then reconciled against the RESOLVED
   * response's screener `row_id` -- a different identity concept entirely --
   * and dropped it on the (near-certain) mismatch, silently losing the
   * `?ledgerTarget=` backlink on the round trip. Screener/edition origins
   * still use this field exactly as before.
   */
  rowId?: string | null;
  /**
   * The edition's own `as_of` (ISO date), required when `origin` is
   * "edition" -- round 2 (FIX 4): an archived edition card must send the
   * visitor back to ITS OWN edition (`/portal/trade-ideas/edition/{asOf}`),
   * never silently to whatever edition happens to be "today" by the time
   * they click "back to idea". Ignored for a "screener" origin (the
   * screener page carries no date of its own).
   */
  editionAsOf?: string | null;
}

// Mirrors market-pulse's own `getTickerDashboardSymbol` (LiveOptionsTab.tsx /
// ZeroDTETab.tsx): SPX/SPXW have no direct options chain of their own on the
// ticker dashboard's data source, so both already resolve to SPY there. Not
// re-derived from those modules to avoid a cross-domain import; this is the
// SAME two-line mapping, kept in sync by inspection (this ticket doesn't
// consolidate that pre-existing duplication -- out of scope).
function tickerDashboardSymbol(ticker: string): string {
  const normalized = (ticker || "").trim().toUpperCase();
  return normalized === "SPX" || normalized === "SPXW" ? "SPY" : normalized;
}

/**
 * Round 2 (FIX 2, instrument binding): does building a ticker-context link
 * for `ticker` require an underlying REWRITE (SPX/SPXW -> SPY)? When it
 * does, the idea's OWN instrument (SPX/SPXW options) and the chart's actual
 * underlying (SPY) are genuinely different instruments -- attaching idea
 * context to that chart would silently misrepresent an SPX idea's strikes/
 * breakeven/exit-rule as if they applied to SPY. The chosen, documented
 * behavior: link to the SPY chart CONTEXT-FREE (never attach ideaLedgerId)
 * rather than not offering the link at all, since the ticker view itself is
 * still useful for the underlying the idea trades against -- just without a
 * banner/overlay that would misattribute the idea's specifics to SPY.
 */
function requiresUnderlyingRewrite(ticker: string): boolean {
  const normalized = (ticker || "").trim().toUpperCase();
  return normalized === "SPX" || normalized === "SPXW";
}

/**
 * Build the shareable "Open <ticker> ticker view" deep link for a Desk idea.
 * Returns `null` (honest-absence, never a guessed/partial link) when the
 * idea has no `ledgerId` -- the ticket requires the outbound action to be
 * "disabled honestly when no ledger_id on the row," never a link to a
 * ledger id that does not exist.
 *
 * Round 2 (FIX 2): when `ticker` requires the SPX/SPXW -> SPY underlying
 * rewrite, the link still points at the SPY chart but carries NO idea
 * context params at all -- see `requiresUnderlyingRewrite`'s docstring.
 */
export function buildTickerContextLink(input: TickerContextLinkInput): string | null {
  const ticker = tickerDashboardSymbol(input.ticker);
  if (!ticker || !input.ledgerId) return null;
  if (requiresUnderlyingRewrite(input.ticker)) {
    return `/ticker/${encodeURIComponent(ticker)}`;
  }
  const params = new URLSearchParams();
  params.set(TICKER_CONTEXT_LEDGER_ID_PARAM, input.ledgerId);
  params.set(TICKER_CONTEXT_ORIGIN_PARAM, input.origin);
  // JS-1094 (demo blocker FIX 2): a "ledger" origin NEVER carries `rowId` --
  // its row identity IS `ledgerId` (set above), a fundamentally different
  // concept from a screener/edition `row_id`. Carrying it here would let the
  // ticker dashboard's `row_id` reconciliation (meant for screener/edition
  // origins only) silently null it out on the near-certain mismatch against
  // the resolved response's screener `row_id`, losing the ledger backlink.
  if (input.origin !== "ledger" && input.rowId) {
    params.set(TICKER_CONTEXT_ROW_ID_PARAM, input.rowId);
  }
  if (input.origin === "edition" && input.editionAsOf) {
    params.set(TICKER_CONTEXT_AS_OF_PARAM, input.editionAsOf);
  }
  return `/ticker/${encodeURIComponent(ticker)}?${params.toString()}`;
}

export interface ResolvedTickerContextParams {
  ledgerId: string;
  origin: TickerContextOrigin | null;
  rowId: string | null;
  /** The edition's `as_of` the link carried, when `origin` is "edition". */
  editionAsOf: string | null;
}

/**
 * Read the ticker-context query params back off a `URLSearchParams` (e.g.
 * from `useSearchParams()`). Returns `null` when `ideaLedgerId` is absent --
 * the context-free case, which is the overwhelmingly common one (most
 * `/ticker/:ticker` visits carry no idea context at all).
 */
export function readTickerContextParams(searchParams: URLSearchParams): ResolvedTickerContextParams | null {
  const ledgerId = searchParams.get(TICKER_CONTEXT_LEDGER_ID_PARAM)?.trim();
  if (!ledgerId) return null;
  const rawOrigin = searchParams.get(TICKER_CONTEXT_ORIGIN_PARAM);
  const origin: TickerContextOrigin | null =
    rawOrigin === "screener" || rawOrigin === "edition" || rawOrigin === "ledger" ? rawOrigin : null;
  const rowId = searchParams.get(TICKER_CONTEXT_ROW_ID_PARAM)?.trim() || null;
  const editionAsOf = searchParams.get(TICKER_CONTEXT_AS_OF_PARAM)?.trim() || null;
  return { ledgerId, origin, rowId, editionAsOf: origin === "edition" ? editionAsOf : null };
}

export interface BackToIdeaHrefInput {
  origin: TickerContextOrigin | null;
  /**
   * The row identity to send the visitor back to, for a "screener" or
   * "edition" origin. Round 2 (FIX 4): this MUST be the RESOLVED response's
   * OWN `row_id` -- never trust a URL-supplied row id the visitor (or a
   * stale/tampered link) could have set to anything. Callers are expected to
   * have already dropped a URL-supplied id that disagreed with the resolved
   * response before calling this (see `reconcileBackToIdeaRowId`).
   *
   * JS-1094 (demo blocker FIX 2): NOT used for a "ledger" origin -- see
   * `ledgerId` below.
   */
  rowId: string | null;
  /** The edition's own `as_of`, required to build an edition-origin link. */
  editionAsOf: string | null;
  /**
   * JS-1094 (demo blocker FIX 2): the ledger row's identity to send the
   * visitor back to, for a "ledger" origin ONLY. This is always the
   * RESOLVED response's OWN `ledger_id` -- the exact id the context fetch
   * was keyed on (`GET .../idea-context/{ledger_id}`), so unlike `rowId`
   * above it needs no reconciliation against a URL-supplied value: there is
   * no untrusted "ledger_id the visitor typed" in play, only the one the
   * resolved response was fetched by. Ignored for any other origin.
   */
  ledgerId?: string | null;
}

/**
 * Round 2 (FIX 4): reconcile a URL-supplied `ideaRowId` against the
 * RESOLVED response's own `row_id` -- returns the id ONLY when they match
 * (or the URL supplied none at all), `null` otherwise. A mismatch means the
 * link was hand-edited or stale; the honest response is to drop the
 * untrusted value rather than build a back-link that reopens the WRONG
 * row's drawer/card.
 */
export function reconcileBackToIdeaRowId(
  urlSuppliedRowId: string | null,
  resolvedRowId: string | null,
): string | null {
  if (!urlSuppliedRowId) return resolvedRowId ?? null;
  if (!resolvedRowId || urlSuppliedRowId !== resolvedRowId) return null;
  return resolvedRowId;
}

/**
 * Round 2 (adversarial review, MED FIX 5 -- ledger targeting): the ONE query
 * param used to target an exact ledger row for highlighting -- on the ledger
 * page itself (a drawdown peak/trough link, a "back to idea" link FROM the
 * ticker-context seam). Deliberately a SEPARATE param from `openRowId`
 * (`TICKER_CONTEXT_ROW_ID_PARAM`'s sibling below): `row_id` is a UI-SURFACE
 * identity concept (which screener/edition card or drawer to reopen -- see
 * `reconcileBackToIdeaRowId`'s doc comment), a fundamentally different thing
 * from `ledger_id` (a ledger COLLECTION document's primary key). The ledger
 * page consumes this key via the backend's own exact-id equality filter
 * (`GET .../outcome-ledger/rows?ledger_id=...`, round 2 FIX 5's additive
 * backend param) -- never by reusing the row_id reconciliation contract for
 * a lookup it was never designed to express.
 */
export const LEDGER_TARGET_PARAM = "ledgerTarget";

/**
 * Build the "back to idea" href for a resolved context -- the origin
 * surface's own URL, carrying the RECONCILED row id as a query param so
 * that surface can reopen the right drawer/card. Returns `null` when
 * `origin` didn't resolve to a known surface (an old/malformed link) -- the
 * caller degrades to omitting the back-link, never guessing a destination.
 *
 * Round 2 (FIX 4): an edition-origin link ALWAYS routes to
 * `/portal/trade-ideas/edition/{asOf}` (the SOURCE edition), never the bare
 * `/portal/trade-ideas/edition` ("today") -- an archived card must return to
 * the edition it actually came from, not whatever edition is current by the
 * time the visitor clicks back. Falls back to the bare (today) edition path
 * only when no `editionAsOf` was ever carried (an older link, or a
 * same-session "today" view that never needed one).
 *
 * The screener/edition origin surfaces read this SAME `openRowId` param
 * themselves (see `MultiStrategyScreenTable.tsx`'s and `EditionPage.tsx`'s
 * own `useSearchParams` reads, added alongside this deep link) to
 * auto-expand the matching drawer / scroll to the matching card on arrival.
 *
 * Round 2 (FIX 5): a "ledger" origin NEVER uses `openRowId` -- it uses
 * `LEDGER_TARGET_PARAM` instead (see below).
 *
 * JS-1094 (demo blocker FIX 2): a "ledger" origin now reads `input.ledgerId`
 * (the resolved response's OWN `ledger_id`), NOT `input.rowId`. Previously
 * `input.rowId` did double duty as "the row's ledger_id" for a ledger
 * origin, which meant it had to survive `reconcileBackToIdeaRowId` -- a
 * check designed for a DIFFERENT identity (a screener/edition `row_id`)
 * that this ledger_id would almost never match, silently dropping the
 * backlink. `ledgerId` is never reconciled: it is the exact id the context
 * fetch was keyed on, so it is trustworthy by construction.
 */
export function buildBackToIdeaHref(input: BackToIdeaHrefInput): string | null {
  const query = input.rowId ? `?openRowId=${encodeURIComponent(input.rowId)}` : "";
  if (input.origin === "screener") {
    return `/portal/trade-ideas${query}`;
  }
  if (input.origin === "edition") {
    const base = input.editionAsOf
      ? `/portal/trade-ideas/edition/${encodeURIComponent(input.editionAsOf)}`
      : "/portal/trade-ideas/edition";
    return `${base}${query}`;
  }
  if (input.origin === "ledger") {
    // The ledger page isn't dated the way an edition is -- no as_of param
    // needed. FIX 5: `LEDGER_TARGET_PARAM`, NEVER `openRowId` (see this
    // function's doc comment). JS-1094 FIX 2: sourced from `input.ledgerId`,
    // never the reconciled `input.rowId`.
    const ledgerQuery = input.ledgerId ? `?${LEDGER_TARGET_PARAM}=${encodeURIComponent(input.ledgerId)}` : "";
    return `/portal/trade-ideas/ledger${ledgerQuery}`;
  }
  return null;
}
